/**
 * A component used to show the date picker range.
 * @author Rajkumar.N
 */
import React from "react";
import theme from "../../Theme/theme"
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import PropTypes from "prop-types";

const CALENDAR_IMAGE = require('../../assets/images/dynamicPricing/calendar_icon.png');
const TIMER_IMAGE = require('../../assets/images/dynamicPricing/timer_icon.png');


const TIME_RANGE = "Select Time Range"
const TIME_RANGE_ERROR_NOTE = "Please select valid date range";

const timeFilter = {
    DAY: "Day",
    WEEK: "Week",
    MONTH: "Month",
    QUARTER: "Quarter",
    YEAR: "Year",
    ALL: "All",
}


const style = {
    flexColumnWrapper: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center"
    },
    flexRowWrapper: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        alignItems: "center"
    },
    userFilter: {
        fontSize: 25,
        fontFamily: theme.font.avenirMediumOblique,
        color: theme.color.thickGrey,
        textAlign: 'center',
        borderRadius: 8,
        border: "1px solid " + theme.color.tableSeparatorGrey,
        paddingLeft: 20,
        paddingRight: 20,
        marginTop: 12,
        lineHeight: "40px"
    },
    timerDescription: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        fontSize: 15,
        borderWidth: 0,
        width: 150,
        marginTop: 0,
        lineHeight: "20px"
    },
    flexAlignSpaceBetween: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: 'center'
    },
    filterContainer: {
        width: 380,
        height: "100%",
        flexDirection: "column",
    },
};

export default class DateRangePicker extends React.Component {

    static propTypes = {
        onChangeStartDate: PropTypes.func.isRequired,
        onChangeEndData: PropTypes.func.isRequired,
        onChangeRangeFilter: PropTypes.func.isRequired,
        showCalendarImage: PropTypes.bool
    };

    constructor(props) {
        super(props);
        this.state = {
            selectedTimeFilter: timeFilter.ALL,
            startDate: props.startDate ? moment(props.startDate) : null,
            endDate: props.endDate ? moment(props.endDate) : null,
        };
    }

    render() {
        return this.rangePicker();
    }

    rangePicker = () => {
        let timeFilterComponent = [];
        timeFilterComponent.push(Object.keys(timeFilter).map((filter, index) => {
            const filterText = (Object.keys(timeFilter).length === index + 1)
                ? timeFilter[filter]
                : timeFilter[filter] + " - "
            const filterTextStyle = (timeFilter[filter] === this.state.selectedTimeFilter)
                ? { ...style.filterText, ...{ fontWeight: theme.fontWeight.bold } }
                : style.filterText
            return <div key={"timeFilter" + index} style={filterTextStyle} onClick={() =>
                this.updateSelectedTimeFilter(timeFilter[filter])
            }>{filterText}</div>
        }));
        return (
            <div style={{
                display: 'flex',
                flex: 1,
                justifyContent: 'center',
                backgroundColor: 'white',
                padding: 20,
                ...this.props.style
            }}>
                <div style={{ ...style.filterContainer, ...style.flexAlignSpaceBetween }} >
                    <div style={{ display: "flex", width: "100%", justifyContent: "space-around", marginTop: 30 }}>
                        <div style={this.props.horizontal ? style.flexRowWrapper : style.flexColumnWrapper}>
                            {
                                !this.props.lite && <div style={{ ...style.userFilter, ...{ fontSize: 15, marginTop: 0, borderWidth: 0, lineHeight: "30px" } }}>{TIME_RANGE}</div>
                            }
                            <DatePicker
                                style={{
                                    ...this.props.pickerStyle
                                }}
                                locale="en-us"
                                placeholderText={"From"}
                                selected={this.state.startDate}
                                onChange={(date) => this.handleStartDateChange(date)}
                            />
                            { !this.props.lite && <div style={{ ...style.userFilter, ...style.timerDescription }}><span style={style.separator} />{"to"}<span style={style.separator} /></div> }
                            <DatePicker
                                style={{
                                    ...this.props.pickerStyle
                                }}
                                locale="en-us"
                                placeholderText={"To"}
                                selected={this.state.endDate}
                                onChange={(date) => this.handleEndDateChange(date)}
                            />
                            {this.state.showDateRangeError && <div style={{ color: theme.color.red, marginTop: 10 }}>{TIME_RANGE_ERROR_NOTE}</div>}
                        </div>
                    </div>
                    {
                        !this.props.lite && <div style={{ ...style.filterText, ...{ fontWeight: theme.fontWeight.bold, fontSize: 30 } }}>
                            {this.state.selectedTimeFilter.toUpperCase()}
                        </div>
                    } {
                        !this.props.lite && <div style={{ ...style.flexAlignSpaceBetween, ...{ marginBottom: 25 } }} >
                            {timeFilterComponent}
                        </div>
                    }
                </div>
                {this.props.showCalendarImage && <div style={style.flexColumnWrapper}>
                    <img src={CALENDAR_IMAGE} alt="" style={{ height: 114, width: 77 }} />
                    <img src={TIMER_IMAGE} alt="" style={{ height: 79, width: 79 }} />
                </div>}
            </div>
        )
    }

    updateSelectedTimeFilter(value) {
        let dayCount;
        switch (value) {
            case timeFilter.DAY:
                dayCount = 1;
                break;
            case timeFilter.WEEK:
                dayCount = 7;
                break;
            case timeFilter.MONTH:
                dayCount = 30;
                break;
            case timeFilter.QUARTER:
                dayCount = 120;
                break;
            case timeFilter.YEAR:
                dayCount = 365;
                break;
            case timeFilter.ALL:
            default:
                dayCount = 0
        }
        this.setState({
            startDate: dayCount > 0 ? moment().subtract(dayCount, 'days') : "",
            endDate: dayCount > 0 ? moment() : "",
            selectedTimeFilter: value,
            showDateRangeError: false
        }, () => {
            // Callback after select date picker filter.
            this.props.onChangeRangeFilter(this.state.startDate, this.state.endDate);
        })
    }

    /**
     * Handle the start date change.
     * @param {DATE Object} date 
     */
    handleStartDateChange(date) {
        //update the start date only if it is less than or equal to end date and future date.
        if ((this.props.allowFuture || !moment(date).isAfter(new Date())) && !moment(date).isAfter(this.state.endDate)) {
            this.setState({
                startDate: date,
                showDateRangeError: false
            }, () => {
                // Callback after select start date picker filter.
                this.props.onChangeStartDate(date);
            })
        } else {
            this.setState({
                showDateRangeError: true
            })
        }
    }

    /**
    * Handle the end date change.
    * @param {DATE Object} date 
    */
    handleEndDateChange(date) {
        //update the end date only if it is greater than or equal to start and less than or equal to future date.
        if ((this.props.allowFuture || !moment(date).isAfter(new Date())) && !moment(date).isBefore(this.state.startDate, 'day')) {
            this.setState({
                endDate: date,
                showDateRangeError: false
            }, () => {
                // Callback after select end date picker filter.
                this.props.onChangeEndData(date);
            })
        } else {
            this.setState({
                showDateRangeError: true
            })
        }
    }
}
